<script>
  import './styles/common.css';
  import './styles/components/layout.css';
  
  // 페이지 컴포넌트들
  import Login from './Login.svelte';
  import Signup from './Signup.svelte';
  import SignupComplete from './SignupComplete.svelte';
  import HowToUse from './HowToUse.svelte';
  import GeminiTest from './GeminiTest.svelte';
  import ProjectRegister from './ProjectRegister.svelte';
  import ProjectEdit from './ProjectEdit.svelte';
  import AdminApplicants from './AdminApplicants.svelte';
  import AdminProjectApplicants from './AdminProjectApplicants.svelte';
  import AdminAllApplicants from './AdminAllApplicants.svelte';
  import CompanyApplicants from './CompanyApplicants.svelte';
  import AdminUsers from './AdminUsers.svelte';
  import AdminDashboard from './AdminDashboard.svelte';
  import CommonCodes from './routes/CommonCodes.svelte';
  import CertificateIssue from './CertificateIssue.svelte';

  // 레이아웃 컴포넌트들
  import Navigation from './components/layout/Navigation.svelte';
  import ProjectFilters from './components/layout/ProjectFilters.svelte';
  
  // 프로젝트 관련 컴포넌트들
  import ProjectList from './components/projects/ProjectList.svelte';
  import ProjectDetail from './components/projects/ProjectDetail.svelte';
  
  // 페이지 컴포넌트들
  import MyPage from './components/pages/MyPage.svelte';
  
  // 공통 컴포넌트들
  import LoadingSpinner from './components/common/LoadingSpinner.svelte';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import EmptyState from './components/common/EmptyState.svelte';
  import StatusBadge from './components/common/StatusBadge.svelte';
  import Icon from './components/common/Icon.svelte';
  import BaseModal from './components/modals/BaseModal.svelte';
  import Button from './components/common/Button.svelte';
  import FileUpload from './components/common/FileUpload.svelte';
  
  import { onMount, onDestroy } from 'svelte';
  import { API_BASE_URL, isSuperAdmin, isAdminUser } from './config.js';
  
  let currentPage = 'home';
  let user = null;
  let mypageTab = 'applications';
  let isAdmin = false; // 관리자 권한 여부
  let isCompany = false; // 기업 사용자 여부
  let selectedApplicantId = null; // 지원자 관리에서 선택된 지원자 ID
  let selectedApplicantShowDetail = false; // 지원자 상세 모달 표시 여부

  // localStorage에서 로그인한 사용자 정보 복원
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      user = JSON.parse(storedUser);
      // 사용자 역할 확인
      isAdmin = user?.role === 'admin';
      isCompany = user?.role === 'company';
    } catch (error) {
      console.error('사용자 정보 파싱 오류:', error);
    }
  }

  // 새로고침 후 돌아갈 페이지 확인
  const returnPage = sessionStorage.getItem('returnPage');
  if (returnPage) {
    currentPage = returnPage;
    sessionStorage.removeItem('returnPage');
  }

  // 5분 동안 조작이 없으면 자동 새로고침 (프로젝트 찾기 페이지)
  let inactivityTimer;
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5분

  function resetInactivityTimer() {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    // home 페이지에서만 비활성 타이머 동작
    if (currentPage === 'home') {
      inactivityTimer = setTimeout(() => {
        sessionStorage.setItem('returnPage', 'home');
        location.reload();
      }, INACTIVITY_TIMEOUT);
    }
  }

  // 사용자 정보 수정 관련 상태
  let profileForm = {
    name: '',
    phone: '',
    birthDate: '', // 생년월일 추가
    gender: '', // 성별 추가 (M/F)
    educationLevel: 'none', // 최종학력 추가
    careerYear: 0, // 경력년차 추가
    careerMonth: 0, // 경력월차 추가
    availableFrom: '', // 투입가능일자 추가
    zipcode: '', // 우편번호 추가
    basicAddress: '', // 기본주소 추가
    detailAddress: '', // 상세주소 추가
    recommenderEmail: '' // 추천자 이메일 추가 (읽기 전용)
  };
  let calculatedAge = ''; // 자동 계산된 나이
  let isProfileUpdating = false;
  let profileUpdateMessage = '';

  
  // 성능 최적화를 위한 throttle 함수
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // 날짜 차이 계산 함수 (기간 계산)
  function calculateDuration(startDate, endDate) {
    if (!startDate || !endDate) return null;
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 30) {
        return `${diffDays}일`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        const remainingDays = diffDays % 30;
        return remainingDays > 0 ? `${months}개월 ${remainingDays}일` : `${months}개월`;
      } else {
        const years = Math.floor(diffDays / 365);
        const remainingDays = diffDays % 365;
        const months = Math.floor(remainingDays / 30);
        return months > 0 ? `${years}년 ${months}개월` : `${years}년`;
      }
    } catch (error) {
      console.error('기간 계산 오류:', error);
      return null;
    }
  }
  
  // 날짜 형식 변환 함수 (YYYY.MM.DD 형태로)
  function formatDate(dateString) {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}.${month}.${day}`;
    } catch (error) {
      console.error('날짜 형식 변환 오류:', error);
      return null;
    }
  }
  
  // 프로젝트 기간 표시 함수 - 요청된 형식으로 변경
  function formatProjectPeriod(startDate, endDate, status = null) {
    // status가 'open'인 경우 "채용 시 마감" 반환
    if (status === 'open') {
      return '채용 시 마감';
    }
    
    // status가 'closed'인 경우 "상시채용" 반환
    if (status === 'closed') {
      return '상시채용';
    }
    
    if (!startDate || !endDate) return null;
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      
      const formattedEnd = formatDate(endDate);
      
      // 현재 날짜가 시작일 이후면 "즉시~종료일자" 형태로 표시
      if (today >= start) {
        return `즉시~${formattedEnd}`;
      } else {
        // 그렇지 않으면 "시작일자~종료일자" 형태로 표시
        const formattedStart = formatDate(startDate);
        return `${formattedStart}~${formattedEnd}`;
      }
    } catch (error) {
      console.error('프로젝트 기간 표시 오류:', error);
      return null;
    }
  }
  
  // 채용 마감일 표시 함수 - 마감일만 표시
  function formatRecruitmentPeriod(createdAt, deadlineDate, status = null) {
    // status가 'open'인 경우 "채용 시 마감" 반환
    if (status === 'open') {
      return '채용 시 마감';
    }
    
    // status가 'closed'인 경우 "상시채용" 반환
    if (status === 'closed') {
      return '상시채용';
    }
    
    if (!deadlineDate) return '상시채용';
    
    try {
      const formattedDeadline = formatDate(deadlineDate);
      
      if (!formattedDeadline) return '상시채용';
      
      return formattedDeadline;
    } catch (error) {
      console.error('채용 마감일 표시 오류:', error);
      return '상시채용';
    }
  }
  
  let selectedProject = null; // 선택된 프로젝트 정보
  let projects = []; // 빈 배열로 초기화
  let filteredProjects = []; // 검색 결과를 저장할 배열
  let searchTerm = ''; // 검색어
  let isLoading = false; // 로딩 상태 추가
  let loadError = null; // 에러 상태 추가
  
  // 무한 스크롤 관련 변수
  let displayedProjects = []; // 현재 표시되는 프로젝트 목록
  const itemsPerPage = 16; // 페이지당 로드할 아이템 수 (고정값)
  let scrollPage = 0; // 현재 로드된 페이지 (0부터 시작하여 스크롤 시에만 로드)
  let hasMoreItems = true; // 더 많은 아이템이 있는지 여부
  let isLoadingMore = false; // 추가 로딩 중인지 여부
  let intersectionTarget = null; // Intersection Observer 타겟
  let currentObserver = null; // 현재 활성화된 Observer
  
  // 정렬 옵션 상태 추가
  let sortOption = '최신순';

  
  // 지원 관련 상태 변수
  let applications = []; // 사용자의 지원 현황
  let appStats = { 
    total: 0, 
    applied: 0, 
    reviewing: 0, 
    proceeding: 0,
    accepted: 0,
    rejected: 0,
    project_canceled: 0,
    project_delayed: 0,
    closed: 0, 
    canceled: 0 
  }; // 지원 통계
  let applyLoading = false; // 지원 처리 중 로딩 상태
  let applyMessage = ''; // 지원 시 메시지
  let projectPrice = ''; // 프로젝트 단가 입력
  let availableDate = ''; // 면접 가능일 입력
  let startWorkDate = ''; // 출근 가능일 입력
  let applicationError = null; // 지원 관련 에러
  
  // 지원 현황 필터링을 위한 변수
  let activeFilter = 'all'; // 기본값은 '전체'
  let filteredApplications = []; // 필터링된 지원 목록
  
  // 상세보기 모달 관련 변수
  let isDetailModalOpen = false;
  let selectedApplication = null;
  
  // 상태 옵션 (DB에서 동적 로드, 기본값은 fallback용)
  let statusOptions = [
    { id: 0, label: '신청됨' }
  ];

  // 상태 값 매핑 (문자열 -> 숫자) - DB 로드 후 동적 생성
  let statusMapping = {};

  // 숫자 -> 문자열 매핑 - DB 로드 후 동적 생성
  let reverseStatusMapping = {};

  // 상태 코드를 DB에서 로드하는 함수 (CD008 그룹)
  async function loadStatusCodes() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/codes/details/active/CD008`);
      if (!response.ok) {
        console.warn('상태 코드 로드 실패, 기본값 사용');
        return;
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        // statusOptions 생성
        statusOptions = result.data.map(item => ({
          id: parseInt(item.code_value),
          label: item.code_name
        }));

        // statusMapping 생성 (label을 snake_case로 변환하여 키로 사용)
        statusMapping = {};
        reverseStatusMapping = {};

        result.data.forEach(item => {
          const id = parseInt(item.code_value);
          const key = item.code_name
            .toLowerCase()
            .replace(/[()]/g, '')
            .replace(/\s+/g, '_')
            .replace(/[가-힣]+/g, match => {
              // 한글을 영문 키로 매핑
              const korToEng = {
                '신청됨': 'applied',
                '검토중': 'reviewing',
                '본인확인중': 'verifying',
                '진행중': 'proceeding',
                '합격': 'accepted',
                '불합격': 'rejected',
                '프로젝트취소': 'project_canceled',
                '프로젝트연기': 'project_delayed',
                '마감': 'closed',
                '지원취소': 'canceled',
                '진행어려움단가': 'price_difficulty',
                '진행어려움등급': 'grade_difficulty',
                '진행어려움스킬': 'skill_difficulty',
                '진행어려움경험': 'experience_difficulty'
              };
              const normalized = match.replace(/\s+/g, '');
              return korToEng[normalized] || normalized;
            });

          statusMapping[key] = id;
          reverseStatusMapping[id] = key;
        });

        console.log('[App] 상태 코드 로드 완료:', statusOptions.length, '개');
      }
    } catch (error) {
      console.error('상태 코드 로드 오류:', error);
    }
  }

  // 필터 상태를 저장할 객체
  let filters = {
    categories: {
      web: false,
      app: false,
      server: false,
      frontend: false,
      fullstack: false,
      database: false
    },
    experience: {
      beginner: false,
      intermediate: false,
      advanced: false,
      any: false
    },
    // 기술 스택 필터를 동적으로 관리하기 위해 빈 객체로 변경
    skills: {},
    workTypes: {
      onsite: false,
      other: false
    },
    status: {
      excludeClosed: true, // 마감 제외 필터 추가
      excludeApplied: false // 내가 지원한 공고 제외 필터 추가
    },
    region: {
      selectedCity: ''
    }
  };
  
  // 기술 스택 데이터
  let skillsData = [
    {
      "code": "T010101",
      "name": "JAVA",
      "value": "filter",
      "depth": 2
    },
    {
      "code": "T01010101",
      "name": "JAVA",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010102",
      "name": "Node.js",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010103",
      "name": "React.js",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010104",
      "name": "Vue.js",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010105",
      "name": "TypeScript",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010106",
      "name": "Nexacro",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010107",
      "name": "MiPlatform",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010108",
      "name": "Angular",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T010102",
      "name": "APP",
      "value": "filter",
      "depth": 2
    },
    {
      "code": "T01010201",
      "name": "Android",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010202",
      "name": "IOS(Object-C)",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010203",
      "name": "Kotlin",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010204",
      "name": "Flutter",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010205",
      "name": "React Native",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T010103",
      "name": "PHP/ASP",
      "value": "filter",
      "depth": 2
    },
    {
      "code": "T01010301",
      "name": "PHP",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010302",
      "name": "Laravel",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010303",
      "name": "ASP",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T010104",
      "name": "C/C++",
      "value": "filter",
      "depth": 2
    },
    {
      "code": "T01010401",
      "name": "C",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010402",
      "name": "C#",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010403",
      "name": "C++",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010404",
      "name": "MFC",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010405",
      "name": ".NET",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010406",
      "name": "Python",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010407",
      "name": "Delphi",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T010105",
      "name": "DB",
      "value": "filter",
      "depth": 2
    },
    {
      "code": "T01010501",
      "name": "Oracle",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010502",
      "name": "MSSQL",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010503",
      "name": "MySQL",
      "value": "filter",
      "depth": 3
    },
    {
      "code": "T01010504",
      "name": "MariaDB",
      "value": "filter",
      "depth": 3
    }
  ];
  
  // 펼쳐진 기술 스택 저장 (코드: boolean)
  let expandedSkills = {};
  
  // 검색어가 변경될 때마다 자동으로 필터링 (debounce 적용)
  let searchTimeout;
  let isRegionFiltering = false; // 지역 필터링 중인지 추적
  
  $: if (searchTerm !== undefined && projects.length > 0) {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      filterProjects();
    }, 300); // 300ms 지연
  }
  
  // filteredProjects가 변경될 때마다 무한 스크롤 초기화
  $: if (filteredProjects.length >= 0) {
    initializeInfiniteScroll();
  }
  
  // 기술 스택 토글 함수
  function toggleSkill(code) {
    expandedSkills[code] = !expandedSkills[code];
  }
  
  // 특정 depth의 기술 스택 항목 가져오기
  function getSkillsByDepth(depth) {
    return skillsData.filter(skill => skill.depth === depth);
  }
  
  // 특정 부모 코드의 하위 기술 스택 항목 가져오기
  function getChildSkills(parentCode) {
    // 부모 코드가 T010101인 경우, 하위 코드는 T01010101, T01010102, ... 형태
    return skillsData.filter(skill => 
      skill.code.startsWith(parentCode) && 
      skill.code !== parentCode && 
      skill.code.length === parentCode.length + 2
    );
  }
  
  // 컴포넌트 초기화 시 로컬 스토리지에서 사용자 정보 가져오기
  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        // 사용자 역할 확인
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
        
        // profileForm 초기화
        if (user) {
          // birth_date를 HTML date input 형식으로 변환
          let formattedBirthDate = '';
          if (user.birth_date || user.birthDate) {
            const birthDate = new Date(user.birth_date || user.birthDate);
            if (!isNaN(birthDate.getTime())) {
              const year = birthDate.getFullYear();
              const month = String(birthDate.getMonth() + 1).padStart(2, '0');
              const day = String(birthDate.getDate()).padStart(2, '0');
              formattedBirthDate = `${year}-${month}-${day}`;
            }
          }
          
          profileForm = {
            name: user.name || '',
            phone: user.phone || '',
            birthDate: formattedBirthDate,
            gender: user.gender || '',
            educationLevel: user.education_level || 'none',
            careerYear: user.career_year || 0,
            careerMonth: user.career_month || 0,
            availableFrom: user.available_from ? new Date(user.available_from).toISOString().split('T')[0] : '',
            zipcode: user.zipcode || '',
            basicAddress: user.basic_address || '',
            detailAddress: user.detail_address || ''
          };
        }
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
        localStorage.removeItem('user');
      }
    }
  }
  
  // 페이지 로드 시 프로젝트 데이터 가져오기
  onMount(async () => {
    // 상태 코드 먼저 로드 (DB에서 동적으로)
    await loadStatusCodes();

    await fetchProjects();

    // URL 해시에 따른 초기 페이지 설정
    handleInitialNavigation();

    // 브라우저 뒤로가기/앞으로가기 이벤트 처리
    window.addEventListener('popstate', handlePopState);

    // Navigation 컴포넌트 이벤트 리스너 등록
    window.addEventListener('pagechange', handleNavigationPageChange);
    window.addEventListener('logout', handleNavigationLogout);
    window.addEventListener('logoclick', handleLogoClick);

    // 로그인한 경우 지원 현황 로드
    if (user) {
      await fetchApplications();
      await loadUserFiles();
    }

    // 스크롤 리스너 설정
    setupScrollListener();

    // 1시간마다 강력 새로고침 (배포된 새 버전 자동 적용)
    const reloadInterval = setInterval(() => {
      window.location.reload();
    }, 60 * 60 * 1000); // 1시간 = 3,600,000ms

    // 5분 비활성 타이머 시작 및 이벤트 리스너 등록
    resetInactivityTimer();
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('mousedown', resetInactivityTimer);
    window.addEventListener('keydown', resetInactivityTimer);
    window.addEventListener('scroll', resetInactivityTimer);
    window.addEventListener('touchstart', resetInactivityTimer);

    // 컴포넌트 언마운트 시 interval 정리
    return () => clearInterval(reloadInterval);
  });
  
  // displayedProjects나 filteredProjects가 변경될 때마다 IntersectionObserver 재설정
  $: if (typeof window !== 'undefined' && filteredProjects) {
    setTimeout(() => setupIntersectionObserver(), 100);
  }

  // 컴포넌트 제거 시 이벤트 리스너 제거
  onDestroy(() => {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('pagechange', handleNavigationPageChange);
    window.removeEventListener('logout', handleNavigationLogout);

    // IntersectionObserver 정리
    if (currentObserver) {
      currentObserver.disconnect();
      currentObserver = null;
    }

    // 스크롤 리스너 제거
    removeScrollListener();

    window.removeEventListener('logoclick', handleLogoClick);

    // 비활성 타이머 정리
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    window.removeEventListener('mousemove', resetInactivityTimer);
    window.removeEventListener('mousedown', resetInactivityTimer);
    window.removeEventListener('keydown', resetInactivityTimer);
    window.removeEventListener('scroll', resetInactivityTimer);
    window.removeEventListener('touchstart', resetInactivityTimer);
  });
  
  // Navigation 컴포넌트에서 발생하는 이벤트 처리
  function handleNavigationPageChange(event) {
    setPage(event.detail.page);
  }
  
  function handleNavigationLogout() {
    handleLogout();
  }
  
  // 로고 클릭 시 필터와 검색어 초기화
  function handleLogoClick() {
    resetFilters();
  }
  
  
  // 초기 URL 해시에 따른 페이지 설정
  function handleInitialNavigation() {
    const hash = window.location.hash;
    if (hash) {
      if (hash.startsWith('#project/')) {
        // 프로젝트 상세 페이지
        const projectId = hash.split('/')[1];
        const project = projects.find(p => p.id.toString() === projectId);
        if (project) {
          selectedProject = project;
        } else {
          // 프로젝트를 찾지 못한 경우 목록으로 이동
          window.location.hash = '#projects';
        }
      } else if (hash.startsWith('#admin-applicants/')) {
        // 지원자 관리 페이지 - 특정 지원자 선택 상태
        const parts = hash.split('/');
        const applicantUserId = parts[1];
        if (applicantUserId) {
          currentPage = 'admin-applicants';
          selectedApplicantId = applicantUserId;
          // /detail 접미사가 있으면 상세 모달 자동 열기
          selectedApplicantShowDetail = parts[2] === 'detail';
        } else {
          currentPage = 'admin-applicants';
          selectedApplicantId = null;
          selectedApplicantShowDetail = false;
        }
      } else if (hash === '#projects') {
        // 프로젝트 목록 페이지
        currentPage = 'home';
        selectedProject = null;
      } else {
        // 기타 페이지 (login, signup 등)
        const page = hash.substring(1); // # 제거
        if ([
          'login',
          'signup',
          'signup-complete',
          'how-to-use',
          'gemini-test',
          'project-register',
          'mypage',
          'admin-applicants',
          'admin-project-applicants',
          'admin-all-applicants',
          'company-applicants',
          'admin-users',
          'admin-dashboard',
          'common-codes'
        ].includes(page)) {
          currentPage = page;
          // admin-applicants 페이지로 이동 시 선택된 지원자 초기화
          if (page === 'admin-applicants') {
            selectedApplicantId = null;
          }
        }
      }
    } else {
      // 해시가 없는 경우 기본 페이지(홈)으로 설정하고 해시 추가
      window.history.replaceState({ page: 'home' }, '', '#projects');
    }
  }
  
  // 브라우저의 뒤로가기/앞으로가기 이벤트 처리
  function handlePopState(event) {
    const state = event.state;
    if (state) {
      if (state.page === 'detail' && state.projectId) {
        // 프로젝트 상세 페이지로 이동
        const project = projects.find(p => p.id === state.projectId);
        if (project) {
          selectedProject = project;
          currentPage = 'home';
        }
      } else if (state.page === 'admin-applicants' && state.applicantId) {
        // 지원자 관리 - 특정 지원자 선택 상태로 이동
        currentPage = 'admin-applicants';
        selectedApplicantId = state.applicantId;
        selectedApplicantShowDetail = state.showDetail || false;
      } else if (state.page === 'admin-applicants') {
        // 지원자 관리 - 목록으로 이동
        currentPage = 'admin-applicants';
        selectedApplicantId = null;
        selectedApplicantShowDetail = false;
      } else if (state.page === 'home') {
        // 프로젝트 목록으로 이동
        selectedProject = null;
        currentPage = 'home';
      } else {
        // 기타 페이지로 이동
        currentPage = state.page;
      }
    } else {
      // 상태가 없는 경우 기본값
      currentPage = 'home';
      selectedProject = null;
    }
  }
  
  // 프로젝트 데이터를 API에서 가져오는 함수 (첫 페이지 로드용)
  async function fetchProjects() {
    try {
      isLoading = true;
      loadError = null;
      
      // 토큰이 필요한 경우 추가
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // 기존 방식으로 모든 프로젝트를 가져와서 클라이언트에서 처리
      // 실제 서버 페이지네이션을 사용하려면 fetchProjectsPage 함수를 사용
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'GET',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('프로젝트 데이터를 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      console.log('프로젝트 API 응답:', data);
      
      // API 응답이 배열이 아닌 경우 처리 (data.projects와 같은 하위 객체일 수 있음)
      const projectsData = Array.isArray(data) ? data : 
                          (data.projects ? data.projects : 
                          (data.data ? data.data : []));
      
      // 데이터 형식 변환
      projects = projectsData.map(project => {
        // 마감일 처리 - deadline_date 또는 application_deadline 필드를 우선 사용
        const deadlineDate = project.deadline_date || project.application_deadline || project.deadline || project.end_date || project.endDate;
        const endDate = project.end_date || project.endDate;
        const startDate = project.start_date || project.startDate;
        
        // 프로젝트 상태 결정
        let status = getProjectStatus(deadlineDate || endDate || startDate, project.status);
        
        // 기술 스택 처리 (JSON 문자열인 경우 파싱)
        let skills = project.skills;
        if (typeof skills === 'string') {
          try {
            skills = JSON.parse(skills);
          } catch (e) {
            skills = skills.split(',').map(s => s.trim());
          }
        }
        
        if (!Array.isArray(skills)) {
          skills = [];
        }
        
        return {
          id: project.id,
          title: project.title,
          companyName: project.company || '비공개',
          companyColor: getStatusColor(status),
          status: status,
          type: project.category || '외주',
          workType: project.location ? '상주' : '재택',
          location: project.location,
          duration: calculateDuration(startDate, endDate) || '미정',
          headcount: project.headcount,
          level: project.level || '무관',
          skills: skills,
          deadline: formatProjectDuration(startDate, deadlineDate, project.status) || '상시채용',
          deadlineDate: deadlineDate,
          endDate: endDate,
          startDate: startDate,
          isTimelyLeave: true,
          isPinned: project.is_pinned || false,
          createdAt: project.created_at || project.createdAt || new Date().toISOString(),
          content: [
            `1. 프로젝트명 : ${project.title}`,
            `2. 담당업무 및 업무환경`,
            ...(project.description ? project.description.split('\n') : ['상세 내용 없음'])
          ]
        };
      });
      
      // 기본 필터 적용 (마감 제외 등)
      filterProjects();
      
      console.log('프로젝트 로딩 완료:', {
        totalProjects: projects.length,
        filteredProjects: filteredProjects.length,
        itemsPerPage
      });

      // 로그인한 사용자가 지원한 프로젝트 표시
      markAppliedProjects();

      // (관리자) 프로젝트별 지원자 수 로드
      if (isAdmin) {
        await fetchProjectApplicantCounts();
      }

      // 무한 스크롤 초기화
      initializeInfiniteScroll();
      
    } catch (error) {
      console.error('프로젝트 데이터 로딩 오류:', error);
      loadError = error.message;
      
      // 에러 발생 시 빈 프로젝트 배열 사용
      projects = [];
      filteredProjects = [];
    } finally {
      isLoading = false;
    }
  }

  // 서버 페이지네이션을 사용한 프로젝트 로드 (무한 스크롤용)
  async function fetchProjectsPage(page = 1, limit = 9) {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE_URL}/api/projects?paginated=true&page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('프로젝트 데이터를 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      
      // 데이터 형식 변환
      const formattedProjects = data.projects.map(project => {
        const deadlineDate = project.deadline_date || project.application_deadline || project.deadline || project.end_date || project.endDate;
        const endDate = project.end_date || project.endDate;
        const startDate = project.start_date || project.startDate;
        
        let status = getProjectStatus(deadlineDate || endDate || startDate, project.status);
        
        let skills = project.skills;
        if (typeof skills === 'string') {
          try {
            skills = JSON.parse(skills);
          } catch (e) {
            skills = skills.split(',').map(s => s.trim());
          }
        }
        
        if (!Array.isArray(skills)) {
          skills = [];
        }
        
        return {
          id: project.id,
          title: project.title,
          companyName: project.company || '비공개',
          companyColor: getStatusColor(status),
          status: status,
          type: project.category || '외주',
          workType: project.location ? '상주' : '재택',
          location: project.location,
          duration: calculateDuration(startDate, endDate) || '미정',
          headcount: project.headcount,
          level: project.level || '무관',
          skills: skills,
          deadline: formatProjectDuration(startDate, deadlineDate, project.status) || '상시채용',
          deadlineDate: deadlineDate,
          endDate: endDate,
          startDate: startDate,
          isTimelyLeave: true,
          isPinned: project.is_pinned || false,
          createdAt: project.created_at || project.createdAt || new Date().toISOString(),
          content: [
            `1. 프로젝트명 : ${project.title}`,
            `2. 담당업무 및 업무환경`,
            ...(project.description ? project.description.split('\n') : ['상세 내용 없음'])
          ]
        };
      });

      return {
        projects: formattedProjects,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('페이지네이션 프로젝트 로딩 오류:', error);
      throw error;
    }
  }
  
  // 프로젝트 기간 포맷팅 함수 - "마감 N일 전" 형식으로 변환 (오늘 기준)
  function formatProjectDuration(startDate, deadlineDate, status = null) {
    // status가 'open'인 경우 "채용 시 마감" 반환
    if (status === 'open') {
      return '채용 시 마감';
    }
    
    // status가 'closed'인 경우 "상시채용" 반환
    if (status === 'closed') {
      return '상시채용';
    }
    
    // status가 'lapse'인 경우 "마감" 반환
    if (status === 'lapse') {
      return '마감';
    }
    
    if (!deadlineDate) return null;
    
    try {
      // 오늘 날짜 (한국 시간 기준)
      const today = new Date();
      const deadline = new Date(deadlineDate);
      
      // 시간 차이 계산 (밀리초 단위)
      const timeDiff = deadline.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 0) {
        return '마감';
      } else if (daysDiff === 0) {
        return '오늘 마감';
      } else if (daysDiff === 1) {
        return '마감 1일 전';
      } else if (daysDiff <= 365) {
        return `마감 ${daysDiff}일 전`;
      } else {
        // 1년 이상인 경우 개월 단위로 표시
        const months = Math.floor(daysDiff / 30);
        return `마감 ${months}개월 전`;
      }
    } catch (error) {
      console.error('프로젝트 기간 포맷팅 오류:', error);
      return null;
    }
  }
  
  // 프로젝트 상태 결정 함수 개선
  function getProjectStatus(deadlineDate, projectStatus = null) {
    // 데이터베이스의 status 필드가 있으면 우선 사용
    if (projectStatus) {
      switch (projectStatus) {
        case 'active':
          return '모집중';
        case 'open':
          return '모집중';
        case 'closed':
          return '모집중';
        case 'deleted':
          return '마감';
        default:
          return projectStatus === 'deleted' ? '마감' : '모집중';
      }
    }
    
    // status 필드가 없으면 기존 로직 사용 (deadlineDate 기준)
    if (!deadlineDate) return '모집중';
    
    try {
      const today = new Date();
      const deadline = new Date(deadlineDate);
      
      if (deadline < today) {
        return '마감';
      } else {
        return '모집중';
      }
    } catch (error) {
      console.error('프로젝트 상태 결정 오류:', error);
      return '모집중';
    }
  }
  
  // 테스트 데이터 생성 함수 - API 실패 시에도 실제 데이터만 사용
  function generateTestData() {
    projects = [];
    filteredProjects = [];
  }
  
  // 상태에 따른 색상 반환 함수
  function getStatusColor(status) {
    switch (status) {
      case '모집중':
         return '#3273dc'; // 파란색
       case '마감':
         return '#868e96'; // 회색
       case '종료':
         return '#fa5252'; // 빨간색
       default:
         return '#3273dc'; // 기본 파란색
     }
   }
   
   function setPage(page) {
     currentPage = page;
     
     // 브라우저 history에 상태 추가
     window.history.pushState({ page }, '', `#${page}`);
     
     // home 페이지로 이동 시 프로젝트 목록 새로고침
     if (page === 'home') {
       selectedProject = null; // 상세 페이지 상태 초기화
       fetchProjects();
     }
     
     // 마이페이지로 이동 시 사용자 정보 로드
     if (page === 'mypage' && user) {
       loadUserProfile();
     }
   }
   
   function handleLogout() {
     // 서버에 로그아웃 API 호출 (토큰 블랙리스트 등록)
     const token = localStorage.getItem('token');
     if (token) {
       fetch(`${API_BASE_URL}/api/auth/logout`, {
         method: 'POST',
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       }).catch(err => console.warn('로그아웃 API 호출 실패:', err));
     }
     
     // 로컬 스토리지에서 사용자 정보 및 토큰 삭제
     localStorage.removeItem('user');
     localStorage.removeItem('token');
     user = null;
     isAdmin = false;
     isCompany = false;
     // 홈페이지로 이동
     currentPage = 'home';
   }
   
   function setUser(userData) {
     user = userData;
     // 사용자 역할 설정
     isAdmin = isAdminUser(userData);
     isCompany = userData.role === 'company';
     
     // profileForm 초기화
     if (userData) {
       // birth_date를 HTML date input 형식으로 변환
       let formattedBirthDate = '';
       if (userData.birth_date || userData.birthDate) {
         const birthDate = new Date(userData.birth_date || userData.birthDate);
         if (!isNaN(birthDate.getTime())) {
           const year = birthDate.getFullYear();
           const month = String(birthDate.getMonth() + 1).padStart(2, '0');
           const day = String(birthDate.getDate()).padStart(2, '0');
           formattedBirthDate = `${year}-${month}-${day}`;
         }
       }
       
       profileForm = {
         name: userData.name || '',
         phone: userData.phone || '',
         birthDate: formattedBirthDate,
         gender: userData.gender || '',
         educationLevel: userData.education_level || 'none',
         careerYear: userData.career_year || 0,
         careerMonth: userData.career_month || 0,
         availableFrom: userData.available_from ? new Date(userData.available_from).toISOString().split('T')[0] : '',
         zipcode: userData.zipcode || '',
         basicAddress: userData.basic_address || '',
         detailAddress: userData.detail_address || ''
       };
     }
         
    // 사용자 변경 시 지원 현황 로드
    fetchApplications();
    loadUserFiles();
   }

   // 마이페이지 탭 변경 함수
   function setMypageTab(tab) {
     mypageTab = tab;
     
     // 프로필 탭 선택 시 사용자 정보 로드
     if (tab === 'profile') {
       loadUserProfile();
     }
   }
   
   // 지원 현황 필터링 함수
   function setFilter(filter) {
     activeFilter = filter;
     
     if (filter === 'all') {
       // 전체 보기
       filteredApplications = [...applications];
     } else {
       // 상태별 필터링
       filteredApplications = applications.filter(app => {
         // 상태 값을 숫자로 변환
         let statusNum = app.status;
         if (typeof statusNum === 'string') {
           statusNum = statusMapping[statusNum] !== undefined ? statusMapping[statusNum] : 0;
         }
         
         // 상태별 필터링
         switch (filter) {
           case 'applied':
             return statusNum === 0 || app.status === 'applied';
           case 'reviewing':
             return statusNum === 1 || app.status === 'reviewing';
           case 'proceeding':
             return statusNum === 2;
           case 'closed':
             return statusNum === 7 || app.status === 'closed';
           case 'canceled':
             return statusNum === 8 || app.status === 'canceled';
           default:
             return true;
         }
       });
     }
   }
   
   // 특정 프로젝트의 지원자 목록 가져오기
   async function fetchProjectApplications(projectId) {
     try {
       if (!user || !isAdminUser(user)) {
         return;
       }
       
       const token = localStorage.getItem('token');
       if (!token) return;
       
       const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/applications`, {
         method: 'GET',
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });
       
       if (!response.ok) {
         throw new Error('프로젝트 지원자 목록을 불러오는 데 실패했습니다.');
       }
       
       const data = await response.json();
       console.log('프로젝트 지원자 API 응답:', data);
       
       // 지원자 목록 추출
       const projectApplicants = data.applicants || [];
       
       // 기존 applications 배열에서 현재 프로젝트 지원자 제외
       applications = applications.filter(app => 
         app.projectId !== projectId && 
         app.project_id !== projectId &&
         !(app.project && app.project.id === projectId)
       );
       
       // 새로운 지원자 목록 추가
       applications = [...applications, ...projectApplicants];
       
       // 필터링된 지원 목록 초기화
       setFilter(activeFilter);
       
     } catch (error) {
       console.error('프로젝트 지원자 목록 로딩 오류:', error);
       alert('프로젝트 지원자 목록을 불러오는 데 실패했습니다: ' + error.message);
     }
   }

   function viewProjectDetail(project) {
     selectedProject = project;
     window.scrollTo(0, 0);
     // history 스택에 상태 추가 (브라우저의 뒤로가기를 지원하기 위해)
     window.history.pushState({ page: 'detail', projectId: project.id }, '', `#project/${project.id}`);
     
     // 프로젝트 상세 페이지에서 관리자인 경우 지원자 목록 새로고침
     if (user && isAdminUser(user)) {
       // 해당 프로젝트의 지원자 목록 가져오기
       fetchProjectApplications(project.id);
     }
   }
   
   function closeProjectDetail() {
     selectedProject = null;
     window.history.pushState({ page: 'home' }, '', '#home');
   }

   // 필터링된 프로젝트에서 현재 페이지에 해당하는 데이터만 가져오는 함수
   // 무한 스크롤 초기화 함수
   function initializeInfiniteScroll() {
     // 스크롤 시 로드되도록 초기 상태를 리셋합니다.
     scrollPage = 0;
     displayedProjects = [];
     
     // filteredProjects가 있으면 hasMoreItems를 true로 설정
     hasMoreItems = filteredProjects && filteredProjects.length > 0;
     
     // Intersection Observer 설정
     setTimeout(() => setupIntersectionObserver(), 100);
   }
   
   // 더 많은 아이템 로드 함수
   function loadMoreItems() {
     console.log('loadMoreItems called', { isLoadingMore, hasMoreItems, scrollPage });
     
     if (isLoadingMore || !hasMoreItems) {
       console.log('loadMoreItems blocked:', { isLoadingMore, hasMoreItems });
       return;
     }
     
     if (!filteredProjects || filteredProjects.length === 0) {
       console.log('No filtered projects available');
       hasMoreItems = false;
       return;
     }
     
     isLoadingMore = true;
     console.log('Starting to load more items...');
     
     // 시뮬레이션을 위한 지연 (실제로는 API 호출 시간)
     setTimeout(() => {
       const startIndex = scrollPage * itemsPerPage;
       const endIndex = Math.min(startIndex + itemsPerPage, filteredProjects.length);
       
       console.log('Loading items:', { startIndex, endIndex, totalItems: filteredProjects.length });
       
       if (startIndex < filteredProjects.length) {
         const newItems = filteredProjects.slice(startIndex, endIndex);
         console.log('New items to add:', newItems.length);
         
         // 중복 제거: 이미 displayedProjects에 있는 ID는 제외
         const existingIds = new Set(displayedProjects.map(p => p.id));
         const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
         
         console.log('Unique items after filtering:', uniqueNewItems.length);
         
         displayedProjects = [...displayedProjects, ...uniqueNewItems];
         scrollPage = scrollPage + 1;
         hasMoreItems = endIndex < filteredProjects.length;
         
         console.log('Updated state:', { 
           displayedCount: displayedProjects.length, 
           newScrollPage: scrollPage, 
           hasMoreItems 
         });
       } else {
         hasMoreItems = false;
         console.log('No more items to load');
       }
       
       isLoadingMore = false;
     }, 300); // 0.3초 지연
   }
   
   // Intersection Observer 설정
   function setupIntersectionObserver() {
     if (typeof window === 'undefined') return;
     
     // 기존 Observer가 있다면 정리
     if (currentObserver) {
       currentObserver.disconnect();
       currentObserver = null;
     }
     
     // intersectionTarget이 없으면 나중에 다시 시도
     if (!intersectionTarget) {
       setTimeout(() => setupIntersectionObserver(), 300);
       return;
     }
     
     console.log('Setting up Intersection Observer', { 
       hasMoreItems, 
       isLoadingMore, 
       filteredProjectsLength: filteredProjects?.length,
       displayedProjectsLength: displayedProjects?.length
     });
     
     currentObserver = new IntersectionObserver(
       (entries) => {
         entries.forEach((entry) => {
           console.log('Intersection event:', { 
             isIntersecting: entry.isIntersecting, 
             hasMoreItems, 
             isLoadingMore 
           });
           
           if (entry.isIntersecting && hasMoreItems && !isLoadingMore) {
             console.log('Triggering loadMoreItems');
             loadMoreItems();
           }
         });
       },
       {
         root: null, // 뷰포트를 root로 사용
         rootMargin: '100px',
         threshold: 0.1
       }
     );
     
     currentObserver.observe(intersectionTarget);
     console.log('Intersection Observer attached to target');
   }

   // 추가적인 스크롤 이벤트 핸들러 (throttled)
   const throttledScrollHandler = throttle(() => {
     if (typeof window === 'undefined') return;
     
     const appContainer = document.querySelector('.app-container');
     if (!appContainer) return;
     
     const { scrollTop, scrollHeight, clientHeight } = appContainer;
     const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
     const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
     
     console.log('Scroll event:', { 
       scrollPercentage: Math.round(scrollPercentage * 100), 
       distanceFromBottom,
       hasMoreItems,
       isLoadingMore 
     });
     
     // 스크롤이 하단 200px 이내에 도달하거나 80% 이상일 때 트리거
     if ((distanceFromBottom <= 200 || scrollPercentage >= 0.8) && hasMoreItems && !isLoadingMore) {
       console.log('Scroll trigger activated');
       loadMoreItems();
     } else if (distanceFromBottom <= 50) {
       // 매우 가까이 왔을 때는 조건 무시하고 강제 트리거
       if (hasMoreItems && !isLoadingMore) {
         console.log('Force scroll trigger activated');
         loadMoreItems();
       }
     }
   }, 100);

   // app-container에 스크롤 이벤트 리스너 등록
   function setupScrollListener() {
     if (typeof window === 'undefined') return;
     
     setTimeout(() => {
       const appContainer = document.querySelector('.app-container');
       if (appContainer) {
         appContainer.addEventListener('scroll', throttledScrollHandler);
       }
     }, 300);
   }

   // 스크롤 이벤트 리스너 제거
   function removeScrollListener() {
     if (typeof window === 'undefined') return;
     
     const appContainer = document.querySelector('.app-container');
     if (appContainer) {
       appContainer.removeEventListener('scroll', throttledScrollHandler);
     }
   }

   // 검색어에 따라 프로젝트를 필터링하는 함수
   async function filterProjects() {
     // 이미 로딩 중이거나 지역 필터링 중이면 중복 호출 방지
     if (isLoading || isRegionFiltering) {
       console.log('로딩 중이거나 지역 필터링 중이므로 filterProjects 중복 호출 방지');
       return;
     }
     
     // 무한 루프 방지를 위한 추가 체크
     if (window.filterProjectsRunning) {
       console.log('filterProjects가 이미 실행 중이므로 중복 호출 방지');
       return;
     }
     
     window.filterProjectsRunning = true;
     
     console.log('filterProjects 호출됨, 지역 필터 상태:', {
       selectedCity: filters.region.selectedCity
     });
     
     try {
       // 지역 필터가 설정된 경우 클라이언트에서 필터링
       if (filters.region.selectedCity) {
         console.log('지역 필터가 설정되어 있음, 클라이언트 지역 필터링 진행');
         isRegionFiltering = true;
         await applyRegionFilterOnly();
         return;
       }
     
     console.log('일반 필터링 진행');
     // 기본적으로 모든 프로젝트를 대상으로
     let result = [...projects];
     
     // 검색어 필터링
     if (searchTerm.trim()) {
       const term = searchTerm.toLowerCase().trim();
       result = result.filter(project => {
         // 제목에서 검색
         if ((project.title || '').toLowerCase().includes(term)) return true;
         
         // 회사명에서 검색
         if ((project.companyName || '').toLowerCase().includes(term)) return true;
         
         // 기술 스택에서 검색
         const projectSkills = Array.isArray(project.skills) ? project.skills : [];
         if (projectSkills.some(skill => (skill || '').toLowerCase().includes(term))) return true;
         
         // 카테고리에서 검색
         if ((project.type || '').toLowerCase().includes(term)) return true;
         
         // 장소에서 검색
         if (project.location && project.location.toLowerCase().includes(term)) return true;
         
         return false;
       });
     }
     
     // 체크박스 필터 적용
     // 선택된 필터가 있는지 확인
     const hasActiveFilter = Object.values(filters).some(
       filterGroup => Object.values(filterGroup).some(value => value)
     );
     
     // 선택된 필터가 있을 때만 필터링 적용
     if (hasActiveFilter) {
       result = result.filter(project => {
         // 개발 분야 필터
         if (Object.values(filters.categories).some(val => val)) {
           // 체크된 필터가 하나라도 있으면
           const projectType = project.type || '';
           const categoryMatch = (
             (filters.categories.web && projectType.includes('웹')) ||
             (filters.categories.app && projectType.includes('앱')) ||
             (filters.categories.server && (projectType.includes('서버') || projectType.includes('백엔드'))) ||
             (filters.categories.frontend && projectType.includes('프론트엔드')) ||
             (filters.categories.fullstack && projectType.includes('풀스택')) ||
             (filters.categories.database && projectType.includes('데이터베이스'))
           );
           
           if (!categoryMatch) return false;
         }
         
         // 구분 필터
         if (Object.values(filters.experience).some(val => val)) {
           const projectLevel = project.level || '';
           const expMatch = (
             (filters.experience.beginner && projectLevel.includes('초급')) ||
             (filters.experience.intermediate && projectLevel.includes('중급')) ||
             (filters.experience.advanced && projectLevel.includes('고급')) ||
             (filters.experience.any && projectLevel.includes('무관'))
           );
           
           if (!expMatch) return false;
         }
         
         // 기술 스택 필터 - 동적으로 처리
         if (Object.values(filters.skills).some(val => val)) {
           // 선택된 기술 스택 코드 목록
           const selectedSkillCodes = Object.keys(filters.skills).filter(code => filters.skills[code]);
           
           // 선택된 기술 스택 이름 목록
           const selectedSkillNames = selectedSkillCodes.map(code => {
             const skill = skillsData.find(s => s.code === code);
             return skill ? skill.name.toLowerCase() : '';
           });
           
           // 프로젝트 기술과 선택된 기술 비교
           const projectSkills = Array.isArray(project.skills) ? project.skills : [];
           const skillsMatch = projectSkills.some(projectSkill => {
             const projectSkillLower = (projectSkill || '').toLowerCase();
             return selectedSkillNames.some(selectedSkill => 
               projectSkillLower.includes(selectedSkill.toLowerCase())
             );
           });
           
           if (!skillsMatch) return false;
         }
         
         // 근무 형태 필터
         if (Object.values(filters.workTypes).some(val => val)) {
           const projectWorkType = project.workType || '';
           const workTypeMatch = (
             (filters.workTypes.onsite && projectWorkType === '상주') ||
             (filters.workTypes.other && projectWorkType !== '상주')
           );
           
           if (!workTypeMatch) return false;
         }
         
         // 마감 제외 필터
         if (filters.status.excludeClosed) {
           if (project.status === '마감' || project.status === 'lapse' || project.deadline === '마감') {
             return false;
           }
         }
         
         // 내가 지원한 공고 제외 필터
         if (filters.status.excludeApplied && applications && applications.length > 0) {
           const appliedProjectIds = applications.map(app =>
             app.projectId || app.project_id || (app.project && app.project.id)
           ).filter(id => id !== undefined && id !== null);
           if (appliedProjectIds.includes(project.id)) return false;
         }
         
         // 지역 필터 - 서버사이드 필터링은 별도로 처리되므로 클라이언트에서는 추가 필터링 불필요
         // 지역 필터는 프로젝트 API 호출 시 region 파라미터로 전달됨
         
         return true;
       });
     }
     
     filteredProjects = result;
     
     // 필터링 후 정렬 적용
     sortProjects();
     
     console.log('필터링 완료:', {
       totalProjects: projects.length,
       filteredProjects: filteredProjects.length,
       searchTerm: searchTerm.trim(),
       hasActiveFilter: Object.values(filters).some(
         filterGroup => Object.values(filterGroup).some(value => value)
       )
     });
     
     // 필터링 후 무한 스크롤 초기화
     initializeInfiniteScroll();
     
     } finally {
       // 플래그 해제
       window.filterProjectsRunning = false;
       isRegionFiltering = false;
     }
   }
   
   // 지역 데이터 캐시
   let regionDataCache = {
     cities: null
   };

  // 지역명을 축약형으로 변환하는 함수
  function getShortRegionName(name) {
    if (!name) return '';
    // "특별시", "특별자치도", "북도", "남도", "도" 등의 접미사 제거
    return name.replace(/특별시|특별자치도|북도|남도|도$/, '');
  }

  // 구/군 이름을 기본 지명으로 변환하는 함수
  function getBaseDistrictName(name) {
    if (!name) return '';
    // '구', '군', '시', '읍', '면', '동' 접미사 제거
    return name.replace(/(구|군|시|읍|면|동)$/, '');
  }
   
   // 지역 필터만 적용 (무한 루프 방지)
   async function applyRegionFilterOnly() {
     try {
       console.log('지역 필터만 적용 시작');
       
       // 기존 프로젝트가 없으면 먼저 로드
       if (projects.length === 0) {
         await fetchProjects();
       }
       
       // 원본 프로젝트에서 지역 필터링
       let result = [...projects];
       
       // 선택된 지역의 이름을 가져오기 (캐시 사용)
       let regionName = '';
       
       if (filters.region.selectedCity) {
         // 시가 선택된 경우
         if (!regionDataCache.cities) {
           console.log('시 데이터 캐시 미스, API 호출');
           const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
           const apiResult = await response.json();
           if (apiResult.success && Array.isArray(apiResult.data)) {
             regionDataCache.cities = apiResult.data;
           }
         }
         
         if (regionDataCache.cities) {
           const city = regionDataCache.cities.find(c => c.code_value === filters.region.selectedCity);
           regionName = city ? city.code_name : '';
         }
       }

       
       const shortRegionName = getShortRegionName(regionName);

       console.log(`지역: ${regionName}(${shortRegionName})`);
       
       if (shortRegionName) {
         result = result.filter(project => {
           const projectLocation = (project.location || '').normalize('NFC').replace(/\s+/g, ' ').trim();
           
           // 시/도 이름은 항상 확인
           const regionMatch = projectLocation.includes(shortRegionName.normalize('NFC'));
           
           return regionMatch;
         });
         console.log('지역 필터링 결과:', result.length, '개');
       }
       
       // 다른 필터들도 적용
       result = applyOtherFilters(result);
       
       filteredProjects = result;
       sortProjects();
       
       console.log('지역 필터만 적용 완료');
       
     } catch (error) {
       console.error('지역 필터만 적용 오류:', error);
       filteredProjects = [...projects];
     } finally {
       // 지역 필터링 완료 후 플래그 즉시 해제
       isRegionFiltering = false;
       window.filterProjectsRunning = false;
       
       // 무한 스크롤 초기화는 약간 지연
       setTimeout(() => {
         initializeInfiniteScroll();
       }, 100);
     }
   }
   
   // 지역 외 다른 필터들 적용
   function applyOtherFilters(projectList) {
     console.log('applyOtherFilters 호출됨, 입력 프로젝트 수:', projectList.length);
     let result = [...projectList];
     
     // 검색어 필터링
     if (searchTerm.trim()) {
       const term = searchTerm.toLowerCase().trim();
       result = result.filter(project => {
         if ((project.title || '').toLowerCase().includes(term)) return true;
         if ((project.companyName || '').toLowerCase().includes(term)) return true;
         
         const projectSkills = Array.isArray(project.skills) ? project.skills : [];
         if (projectSkills.some(skill => (skill || '').toLowerCase().includes(term))) return true;
         
         if ((project.type || '').toLowerCase().includes(term)) return true;
         if (project.location && project.location.toLowerCase().includes(term)) return true;
         return false;
       });
     }
     
     // 다른 필터들 적용 (지역, 상태 제외)
     const hasActiveFilter = Object.keys(filters).some(filterGroup => {
       if (filterGroup === 'region' || filterGroup === 'status') return false; // 지역, 상태 필터 제외
       return Object.values(filters[filterGroup]).some(value => value);
     });
     
     console.log('활성 필터 존재 여부:', hasActiveFilter);
     
     // 상태 필터는 별도로 항상 적용
     if (filters.status.excludeClosed) {
       result = result.filter(project => {
         if (project.status === '마감' || project.status === 'lapse' || project.deadline === '마감') {
           return false;
         }
         return true;
       });
     }
     
     if (filters.status.excludeApplied && applications && applications.length > 0) {
       const appliedProjectIds = applications.map(app =>
         app.projectId || app.project_id || (app.project && app.project.id)
       ).filter(id => id !== undefined && id !== null);
       result = result.filter(project => !appliedProjectIds.includes(project.id));
     }
     
     if (hasActiveFilter) {
       result = result.filter(project => {
         // 개발 분야 필터
         if (Object.values(filters.categories).some(val => val)) {
           const projectType = project.type || '';
           const categoryMatch = (
             (filters.categories.web && projectType.includes('웹')) ||
             (filters.categories.app && projectType.includes('앱')) ||
             (filters.categories.server && (projectType.includes('서버') || projectType.includes('백엔드'))) ||
             (filters.categories.frontend && projectType.includes('프론트엔드')) ||
             (filters.categories.fullstack && projectType.includes('풀스택')) ||
             (filters.categories.database && projectType.includes('데이터베이스'))
           );
           if (!categoryMatch) return false;
         }
         
         // 경험 수준 필터
         if (Object.values(filters.experience).some(val => val)) {
           const projectLevel = project.level || '';
           const expMatch = (
             (filters.experience.beginner && projectLevel.includes('초급')) ||
             (filters.experience.intermediate && projectLevel.includes('중급')) ||
             (filters.experience.advanced && projectLevel.includes('고급')) ||
             (filters.experience.any && projectLevel.includes('무관'))
           );
           if (!expMatch) return false;
         }
         
         // 기술 스택 필터
         if (Object.values(filters.skills).some(val => val)) {
           const selectedSkillCodes = Object.keys(filters.skills).filter(code => filters.skills[code]);
           const selectedSkillNames = selectedSkillCodes.map(code => {
             const skill = skillsData.find(s => s.code === code);
             return skill ? skill.name.toLowerCase() : '';
           });
           
           const projectSkills = Array.isArray(project.skills) ? project.skills : [];
           const skillsMatch = projectSkills.some(projectSkill => {
             const projectSkillLower = (projectSkill || '').toLowerCase();
             return selectedSkillNames.some(selectedSkill => 
               projectSkillLower.includes(selectedSkill.toLowerCase())
             );
           });
           if (!skillsMatch) return false;
         }
         
         // 근무 형태 필터
         if (Object.values(filters.workTypes).some(val => val)) {
           const projectWorkType = project.workType || '';
           const workTypeMatch = (
             (filters.workTypes.onsite && projectWorkType === '상주') ||
             (filters.workTypes.other && projectWorkType !== '상주')
           );
           if (!workTypeMatch) return false;
         }
         
         // 상태 필터는 위에서 이미 처리됨
         
         return true;
       });
     }
     
     return result;
   }
   
   // 간단한 지역 필터링 (무한 루프 방지)
   async function simpleRegionFilter() {
     try {
       isLoading = true;
       isRegionFiltering = true; // 지역 필터링 시작
       console.log('간단한 지역 필터링 시작');
       
       // 전체 프로젝트를 먼저 로드 (지역 필터 없이)
       const token = localStorage.getItem('token');
       const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
       
       const response = await fetch(`${API_BASE_URL}/api/projects`, {
         method: 'GET',
         headers: {
           ...headers,
           'Content-Type': 'application/json'
         }
       });
       
       if (response.ok) {
         const data = await response.json();
         if (data.success && data.projects) {
           // 전체 프로젝트 로드
           projects = data.projects.map(project => ({
             ...project,
             skills: Array.isArray(project.skills) ? project.skills : (project.skills ? project.skills.split(',').map(s => s.trim()) : [])
           }));
           
           console.log('전체 프로젝트 로드 완료:', projects.length, '개');
           
           // 클라이언트에서 지역 필터링
           await applyRegionFilterClientSide();
         }
       }
     } catch (error) {
       console.error('간단한 지역 필터링 오류:', error);
       projects = [];
       filteredProjects = [];
     } finally {
       isLoading = false;
       isRegionFiltering = false; // 지역 필터링 종료
       console.log('간단한 지역 필터링 완료');
     }
   }
   
   // 클라이언트 사이드 지역 필터링
   async function applyRegionFilterClientSide() {
     try {
       console.log('클라이언트 사이드 지역 필터링 시작');
       
       // 선택된 지역의 이름을 가져오기
       let regionName = '';
       
       if (filters.region.selectedCity) {
         const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
         const result = await response.json();
         if (result.success && Array.isArray(result.data)) {
           const city = result.data.find(c => c.code_value === filters.region.selectedCity);
           regionName = city ? city.code_name : '';
         }
       }
       
       console.log('지역 이름:', regionName);
       
       if (regionName) {
         // 지역 이름으로 클라이언트에서 필터링
         const filteredByRegion = projects.filter(project => {
           return project.location && project.location.includes(regionName);
         });
         
         console.log('지역 필터링 결과:', filteredByRegion.length, '개');
         
         // 필터링된 프로젝트를 projects에 설정
         projects = filteredByRegion;
       }
       
       // 다른 필터들 적용하지 않음 (이미 applyRegionFilterOnly에서 처리됨)
       console.log('클라이언트 사이드 지역 필터링 완료');
       
     } catch (error) {
       console.error('클라이언트 사이드 지역 필터링 오류:', error);
     }
   }
   
   // 지역 필터가 적용된 프로젝트 조회
   async function fetchProjectsWithRegionFilter() {
     // 이미 로딩 중이면 중복 호출 방지
     if (isLoading) {
       console.log('이미 로딩 중이므로 중복 호출 방지');
       return;
     }
     
     let timeoutId; // 스코프 문제 해결
     
     try {
       isLoading = true;
       
       // 10초 타임아웃 설정
       timeoutId = setTimeout(() => {
         console.error('지역 필터링 타임아웃 (10초)');
         isLoading = false;
       }, 10000);
       console.log('지역 필터링 시작:', { 
         selectedCity: filters.region.selectedCity, 
         selectedDistrict: filters.region.selectedDistrict 
       });
       
       // 선택된 지역의 이름을 가져오기
       let regionName = '';
       
       if (filters.region.selectedCity) {
         // 시 선택된 경우 해당 이름 가져오기
         console.log('시 선택된 상태, 정보 조회 중...');
         const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
         const result = await response.json();
         console.log('시 목록 조회 결과:', result);
         
         if (result.success && Array.isArray(result.data)) {
           const city = result.data.find(c => c.code_value === filters.region.selectedCity);
           regionName = city ? city.code_name : '';
           console.log('선택된 시:', { city, regionName });
         }
       }
       
       console.log('최종 지역명:', regionName);
       
       if (regionName) {
         // 지역 이름으로 프로젝트 조회
         const token = localStorage.getItem('token');
         const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
         
         const projectUrl = `${API_BASE_URL}/api/projects?region=${encodeURIComponent(regionName)}`;
         console.log('프로젝트 API 호출:', projectUrl);
         
         const response = await fetch(projectUrl, {
           method: 'GET',
           headers: {
             ...headers,
             'Content-Type': 'application/json'
           }
         });
         
         console.log('프로젝트 API 응답 상태:', response.status, response.ok);
         
         if (response.ok) {
           const data = await response.json();
           console.log('프로젝트 데이터:', data);
           
           if (data.success && data.projects) {
             projects = data.projects.map(project => ({
               ...project,
               skills: Array.isArray(project.skills) ? project.skills : (project.skills ? project.skills.split(',').map(s => s.trim()) : [])
             }));
             
             console.log('프로젝트 변환 완료:', projects.length, '개');
             
             // 다른 필터들 적용
             applyClientSideFilters();
           } else {
             console.warn('프로젝트 데이터 형식 오류:', data);
             // 빈 결과 처리
             projects = [];
             filteredProjects = [];
             initializeInfiniteScroll();
           }
         } else {
           console.error('프로젝트 API 오류:', response.status, response.statusText);
           // 오류 시 빈 결과 처리
           projects = [];
           filteredProjects = [];
           initializeInfiniteScroll();
         }
       } else {
         console.warn('지역명이 비어있음, 전체 프로젝트 로드');
         // 지역명이 없으면 전체 프로젝트 로드
         await fetchProjects();
       }
     } catch (error) {
       console.error('지역 필터링된 프로젝트 조회 오류:', error);
       // 오류 시 빈 결과 처리
       projects = [];
       filteredProjects = [];
       initializeInfiniteScroll();
     } finally {
       if (timeoutId) {
         clearTimeout(timeoutId);
       }
       isLoading = false;
       console.log('지역 필터링 완료, 로딩 상태 종료');
     }
   }
   
   // 클라이언트 사이드 필터링 (지역 제외)
   function applyClientSideFilters() {
     let result = [...projects];
     
     // 검색어 필터링
     if (searchTerm.trim()) {
       const term = searchTerm.toLowerCase().trim();
       result = result.filter(project => {
         if ((project.title || '').toLowerCase().includes(term)) return true;
         if ((project.companyName || '').toLowerCase().includes(term)) return true;
         
         const projectSkills = Array.isArray(project.skills) ? project.skills : [];
         if (projectSkills.some(skill => (skill || '').toLowerCase().includes(term))) return true;
         
         if ((project.type || '').toLowerCase().includes(term)) return true;
         if (project.location && project.location.toLowerCase().includes(term)) return true;
         return false;
       });
     }
     
     // 다른 필터들 적용 (지역 제외)
     const hasActiveFilter = Object.keys(filters).some(filterGroup => {
       if (filterGroup === 'region') return false; // 지역 필터 제외
       return Object.values(filters[filterGroup]).some(value => value);
     });
     
     if (hasActiveFilter) {
       result = result.filter(project => {
         // 기존 필터링 로직 (지역 제외) - 안전 검사 추가
         if (Object.values(filters.categories).some(val => val)) {
           const projectType = project.type || '';
           const categoryMatch = (
             (filters.categories.web && projectType.includes('웹')) ||
             (filters.categories.app && projectType.includes('앱')) ||
             (filters.categories.server && (projectType.includes('서버') || projectType.includes('백엔드'))) ||
             (filters.categories.frontend && projectType.includes('프론트엔드')) ||
             (filters.categories.fullstack && projectType.includes('풀스택')) ||
             (filters.categories.database && projectType.includes('데이터베이스'))
           );
           if (!categoryMatch) return false;
         }
         
         if (Object.values(filters.experience).some(val => val)) {
           const projectLevel = project.level || '';
           const expMatch = (
             (filters.experience.beginner && projectLevel.includes('초급')) ||
             (filters.experience.intermediate && projectLevel.includes('중급')) ||
             (filters.experience.advanced && projectLevel.includes('고급')) ||
             (filters.experience.any && projectLevel.includes('무관'))
           );
           if (!expMatch) return false;
         }
         
         if (Object.values(filters.skills).some(val => val)) {
           const selectedSkillCodes = Object.keys(filters.skills).filter(code => filters.skills[code]);
           const selectedSkillNames = selectedSkillCodes.map(code => {
             const skill = skillsData.find(s => s.code === code);
             return skill ? skill.name.toLowerCase() : '';
           });
           
           const projectSkills = Array.isArray(project.skills) ? project.skills : [];
           const skillsMatch = projectSkills.some(projectSkill => {
             const projectSkillLower = (projectSkill || '').toLowerCase();
             return selectedSkillNames.some(selectedSkill => 
               projectSkillLower.includes(selectedSkill.toLowerCase())
             );
           });
           if (!skillsMatch) return false;
         }
         
         if (Object.values(filters.workTypes).some(val => val)) {
           const projectWorkType = project.workType || '';
           const workTypeMatch = (
             (filters.workTypes.onsite && projectWorkType === '상주') ||
             (filters.workTypes.other && projectWorkType !== '상주')
           );
           if (!workTypeMatch) return false;
         }
         
         if (filters.status.excludeClosed) {
           if (project.status === '마감' || project.status === 'lapse' || project.deadline === '마감') return false;
         }
         
         if (filters.status.excludeApplied && applications && applications.length > 0) {
           const appliedProjectIds = applications.map(app =>
             app.projectId || app.project_id || (app.project && app.project.id)
           ).filter(id => id !== undefined && id !== null);
           if (appliedProjectIds.includes(project.id)) return false;
         }
         
         return true;
       });
     }
     
     filteredProjects = result;
     sortProjects();
     
     // 지역 필터링이 아닐 때만 로그 출력
     if (!isRegionFiltering) {
       console.log('클라이언트 사이드 필터링 완료:', {
         total: projects.length,
         filtered: filteredProjects.length
       });
     }
     
     initializeInfiniteScroll();
   }
   
   // 검색 필드에서 엔터키 처리
   function handleSearchKeyup(event) {
     if (event.key === 'Enter') {
       // 엔터키 입력 시 debounce 타이머 클리어하고 즉시 검색
       clearTimeout(searchTimeout);
       filterProjects();
     }
   }
   
   // 검색 버튼 클릭 시 즉시 검색 실행
   function handleSearchClick() {
     clearTimeout(searchTimeout);
     filterProjects();
   }
   
   // 필터 초기화 함수 업데이트
   function resetFilters() {
     searchTerm = '';
     
     // 필터 객체 초기화
     Object.keys(filters).forEach(filterGroup => {
       if (filterGroup === 'skills') {
         // 기술 스택 필터는 모든 기술 스택 코드에 대해 false로 설정
         skillsData.forEach(skill => {
           filters.skills[skill.code] = false;
         });
       } else if (filterGroup === 'region') {
         // 지역 필터는 빈 문자열로 초기화
         filters.region.selectedCity = '';
       } else if (filterGroup === 'status') {
         // 상태 필터는 마감 제외(excludeClosed)를 true로 유지
         Object.keys(filters[filterGroup]).forEach(key => {
           filters[filterGroup][key] = key === 'excludeClosed' ? true : false;
         });
       } else {
         Object.keys(filters[filterGroup]).forEach(key => {
           filters[filterGroup][key] = false;
         });
       }
     });
     
     // 확장된 기술 스택 상태도 초기화
     expandedSkills = {};
     
     // 프로젝트 리스트 초기화 (반응형으로 자동 처리됨)
     filteredProjects = [...projects];
     initializeInfiniteScroll();
   }

   // 정렬 옵션에 따라 프로젝트를 정렬하는 함수
   function sortProjects() {
     // 정렬 전 원본 배열 복사
     let sortedProjects = [...filteredProjects];
     
     // 먼저 상단 고정 항목을 우선 정렬하고, 그 다음 정렬 옵션 적용
     sortedProjects.sort((a, b) => {
       // 1. 상단 고정 항목이 우선
       if (a.isPinned && !b.isPinned) return -1;
       if (!a.isPinned && b.isPinned) return 1;
       
       // 2. 둘 다 고정이거나 둘 다 일반인 경우 정렬 옵션 적용
       switch(sortOption) {
         case '최신순':
           // 최신 등록일 순으로 정렬 (내림차순)
           return new Date(b.createdAt) - new Date(a.createdAt);
           
         case '마감임박순':
           // 상시채용과 채용 시 마감은 항상 마지막에 배치
           if ((a.deadline === '상시채용' || a.deadline === '채용 시 마감') && 
               (b.deadline !== '상시채용' && b.deadline !== '채용 시 마감')) return 1;
           if ((a.deadline !== '상시채용' && a.deadline !== '채용 시 마감') && 
               (b.deadline === '상시채용' || b.deadline === '채용 시 마감')) return -1;
           if ((a.deadline === '상시채용' || a.deadline === '채용 시 마감') && 
               (b.deadline === '상시채용' || b.deadline === '채용 시 마감')) return 0;
           
           // 이미 마감된 프로젝트는 마지막으로
           const aIsClosed = a.status === '마감' || a.status === 'lapse' || a.deadline === '마감';
           const bIsClosed = b.status === '마감' || b.status === 'lapse' || b.deadline === '마감';
           if (aIsClosed && !bIsClosed) return 1;
           if (!aIsClosed && bIsClosed) return -1;
           
           // 종료일자 기준으로 정렬
           if (a.endDate && b.endDate) {
             return new Date(a.endDate) - new Date(b.endDate);
           }
           
           return 0;
           
         case '마감순':
           // 마감된 프로젝트를 먼저 보여줌
           const aIsClosedInOrder = a.status === '마감' || a.status === 'lapse' || a.deadline === '마감';
           const bIsClosedInOrder = b.status === '마감' || b.status === 'lapse' || b.deadline === '마감';
           if (aIsClosedInOrder && !bIsClosedInOrder) return -1;
           if (!aIsClosedInOrder && bIsClosedInOrder) return 1;
           return 0;
           
         default:
           return 0;
       }
     });
     
     filteredProjects = sortedProjects;
   }
   
   // 정렬 옵션 변경 함수
   function handleSortChange(option) {
     sortOption = option;
     sortProjects();
     initializeInfiniteScroll();
   }
  
  // 필터링 상태가 변경될 때마다 자동으로 호출될 함수 (무한 루프 방지를 위해 제거)
  // 필터 상태는 명시적으로 호출할 때만 적용

  // selectedProject 상태가 변경될 때마다 자동으로 실행되는 반응형 코드는 제거
  // (closeProjectDetail과 setPage 함수에서 명시적으로 처리함)

   // API 호출 실패 시 빈 데이터 사용
   function useDefaultProjects() {
     projects = [];
     filteredProjects = [];
   }

   // 사용자의 지원 현황 불러오기
   async function fetchApplications() {
     try {
       const token = localStorage.getItem('token');
       if (!token || !user) return;
       
       // 관리자인 경우 모든 지원 현황 가져오기
       const apiUrl = isAdminUser(user)
         ? `${API_BASE_URL}/api/applications/all`
         : `${API_BASE_URL}/api/applications`;
       
       const response = await fetch(apiUrl, {
         method: 'GET',
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });
       
       if (!response.ok) {
         throw new Error('지원 현황을 불러오는 데 실패했습니다.');
       }
       
       const data = await response.json();
       console.log('지원 현황 API 응답:', data);
       
       // applications 데이터 추출 (배열 또는 객체 속성)
       if (Array.isArray(data)) {
         applications = data;
       } else if (data.applications && Array.isArray(data.applications)) {
         applications = data.applications;
       } else if (data.data && Array.isArray(data.data)) {
         applications = data.data;
       } else if (data.data && data.data.applications && Array.isArray(data.data.applications)) {
         applications = data.data.applications;
       } else {
         applications = [];
         console.warn('API 응답에서 applications 배열을 찾을 수 없습니다.');
       }
       
       // 통계 초기화
       appStats = { 
         total: applications.length, 
         applied: 0, 
         reviewing: 0, 
         proceeding: 0,
         accepted: 0,
         rejected: 0,
         project_canceled: 0,
         project_delayed: 0,
         closed: 0, 
         canceled: 0 
       };
       
       // 상태별 통계 계산
       applications.forEach(app => {
         // 상태 값을 숫자로 변환
         let statusNum = app.status;
         if (typeof statusNum === 'string') {
           statusNum = statusMapping[statusNum] !== undefined ? statusMapping[statusNum] : 0;
         }
         
         // 다양한 필드명에서 프로젝트명 추출하여 표준화
         if (!app.projectTitle) {
           app.projectTitle = app.project_title || app.projectName || app.title || 
                           (app.project && app.project.title) || null;
         }
         
         // 상태별 카운트 증가
         switch (statusNum) {
           case 0:
             appStats.applied++;
             break;
           case 1:
             appStats.reviewing++;
             break;
           case 2:
             appStats.proceeding++;
             break;
           case 3:
             appStats.accepted++;
             break;
           case 4:
             appStats.rejected++;
             break;
           case 5:
             appStats.project_canceled++;
             break;
           case 6:
             appStats.project_delayed++;
             break;
           case 7:
             appStats.closed++;
             break;
           case 8:
             appStats.canceled++;
             break;
           default:
             // 기존 문자열 상태 처리 (이전 버전 호환성)
             if (app.status === 'applied') appStats.applied++;
             else if (app.status === 'reviewing') appStats.reviewing++;
             else if (app.status === 'closed') appStats.closed++;
             else if (app.status === 'canceled') appStats.canceled++;
         }
       });
       
       // 필터링된 지원 목록 초기화
       setFilter(activeFilter);
       
       // 지원 여부 표시 업데이트
       markAppliedProjects();
       
     } catch (error) {
       console.error('지원 현황 로딩 오류:', error);
       applicationError = error.message;
    }
  }
  
  // 프로젝트 지원 신청
  async function applyToProject() {
    try {
      if (!user) {
        // 로그인되지 않은 경우 로그인 페이지로 이동
        setPage('login');
        return;
      }
      
      if (!selectedProject) return;
      
      applyLoading = true;
      applicationError = null;
      
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: selectedProject.id,
          message: applyMessage,
          price: projectPrice ? parseInt(getNumericValue(projectPrice)) : null, // 단가 정보 추가 (콤마 제거)
          availableDate: availableDate || null, // 면접 가능일 추가
          startWorkDate: startWorkDate || null // 출근 가능일 추가
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '지원 신청에 실패했습니다.');
      }
      
      // 지원 성공 후 지원 현황 새로고침
      await fetchApplications();
      
      // 성공 메시지 표시
      alert('프로젝트 지원이 완료되었습니다.');
      applyMessage = '';
      projectPrice = '';
      availableDate = '';
      startWorkDate = '';
      
    } catch (error) {
      console.error('프로젝트 지원 오류:', error);
      applicationError = error.message;
      alert(error.message || '지원 신청 중 오류가 발생했습니다.');
    } finally {
      applyLoading = false;
    }
  }
  
  // 지원 취소
  async function cancelApplication(applicationId) {
    try {
      if (!confirm('정말로 지원을 취소하시겠습니까?')) {
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId }) // 비어있지 않은 JSON 본문 추가
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '지원 취소에 실패했습니다.');
      }
      
      // 지원 현황 새로고침
      await fetchApplications();
      
    } catch (error) {
      console.error('지원 취소 오류:', error);
      alert(error.message || '지원 취소 중 오류가 발생했습니다.');
    }
  }
  
  // 상세보기 모달 닫기
  function closeDetailModal() {
    isDetailModalOpen = false;
    selectedApplication = null;
  }
  
  // 프로젝트 단가 숫자만 입력되도록 검증 및 콤마 포맷팅
  function validateNumber(event) {
    let value = event.target.value;
    // 숫자와 콤마만 허용
    value = value.replace(/[^0-9,]/g, '');
    // 콤마 제거 후 숫자만 추출
    const numericValue = value.replace(/,/g, '');
    
    if (numericValue) {
      // 천 단위 콤마 추가
      const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      event.target.value = formattedValue;
      projectPrice = formattedValue;
    } else {
      event.target.value = '';
      projectPrice = '';
    }
  }
  
  // 숫자 값만 추출하는 함수 (API 전송 시 사용)
  function getNumericValue(formattedValue) {
    return formattedValue ? formattedValue.replace(/,/g, '') : '';
  }
  
  // 파일 업로드 관련 상태
  let userFiles = [];
  let isFileLoading = false;
  
  // 생년월일로부터 나이 계산
  function calculateAge(birthDateString) {
    if (!birthDateString) {
      calculatedAge = '';
      return;
    }
    
    const today = new Date();
    const birthDate = new Date(birthDateString);
    
    if (birthDate > today) {
      calculatedAge = '';
      return;
    }
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    calculatedAge = age.toString();
  }
  
  // 생년월일 변경 시 나이 자동 계산
  $: if (profileForm.birthDate) {
    calculateAge(profileForm.birthDate);
  }
  
  // 사용자 파일 목록 로드
  async function loadUserFiles() {
    try {
      if (!user) return;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/files/my-files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        userFiles = data.files || [];
      }
    } catch (error) {
      console.error('파일 목록 로드 오류:', error);
    }
  }
  
  // 사용자 정보 로드
  async function loadUserProfile() {
    try {
      if (!user) return;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // birth_date를 HTML date input 형식으로 변환 (시간대 문제 해결)
        let formattedBirthDate = '';
        if (userData.birth_date || userData.birthDate) {
          const birthDate = new Date(userData.birth_date || userData.birthDate);
          if (!isNaN(birthDate.getTime())) {
            // 로컬 시간을 사용하여 yyyy-MM-dd 형식으로 변환
            const year = birthDate.getFullYear();
            const month = String(birthDate.getMonth() + 1).padStart(2, '0');
            const day = String(birthDate.getDate()).padStart(2, '0');
            formattedBirthDate = `${year}-${month}-${day}`;
          }
        }
        
        profileForm = {
          name: userData.name || '',
          phone: userData.phone || '',
          birthDate: formattedBirthDate,
          gender: userData.gender || '',
          educationLevel: userData.education_level || 'none',
          careerYear: userData.career_year || 0,
          careerMonth: userData.career_month || 0,
          availableFrom: userData.available_from ? new Date(userData.available_from).toISOString().split('T')[0] : '',
          zipcode: userData.zipcode || '',
          basicAddress: userData.basic_address || '',
          detailAddress: userData.detail_address || '',
          recommenderEmail: userData.recommender_email || '' // 추천자 이메일 추가 (읽기 전용)
        };
        
        // 기존 user 객체도 업데이트
        user = { ...user, ...userData };
        localStorage.setItem('user', JSON.stringify(user));
      }
    } catch (error) {
      console.error('사용자 정보 로드 오류:', error);
    }
  }
  
  // 사용자 정보 업데이트
  async function updateUserProfile() {
    try {
      if (!user) return;
      
      isProfileUpdating = true;
      profileUpdateMessage = '';
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileForm.name,
          phone: profileForm.phone,
          birthDate: profileForm.birthDate, // 생년월일 추가
          gender: profileForm.gender,
          educationLevel: profileForm.educationLevel, // 최종학력 추가
          careerYear: profileForm.careerYear, // 경력년차 추가
          careerMonth: profileForm.careerMonth, // 경력월차 추가
          availableFrom: profileForm.availableFrom, // 투입가능일자 추가
          zipcode: profileForm.zipcode, // 우편번호 추가
          basicAddress: profileForm.basicAddress, // 기본주소 추가
          detailAddress: profileForm.detailAddress // 상세주소 추가
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // 성공 시 사용자 정보 업데이트
        user = { ...user, ...data.user };
        localStorage.setItem('user', JSON.stringify(user));
        profileUpdateMessage = '정보가 성공적으로 업데이트되었습니다.';
        
        // profileForm을 직접 업데이트하여 화면 갱신
        if (data.user) {
          // birth_date를 HTML date input 형식으로 변환 (시간대 문제 해결)
          let formattedBirthDate = '';
          if (data.user.birth_date || data.user.birthDate) {
            const birthDate = new Date(data.user.birth_date || data.user.birthDate);
            if (!isNaN(birthDate.getTime())) {
              // 로컬 시간을 사용하여 yyyy-MM-dd 형식으로 변환
              const year = birthDate.getFullYear();
              const month = String(birthDate.getMonth() + 1).padStart(2, '0');
              const day = String(birthDate.getDate()).padStart(2, '0');
              formattedBirthDate = `${year}-${month}-${day}`;
            }
          }
          
          profileForm = {
            name: data.user.name || profileForm.name,
            phone: data.user.phone || profileForm.phone,
            birthDate: formattedBirthDate || profileForm.birthDate,
            gender: data.user.gender || profileForm.gender,
            educationLevel: data.user.education_level || profileForm.educationLevel,
            careerYear: data.user.career_year || profileForm.careerYear,
            careerMonth: data.user.career_month || profileForm.careerMonth,
            availableFrom: data.user.available_from ? new Date(data.user.available_from).toISOString().split('T')[0] : (profileForm.availableFrom || ''),
            zipcode: data.user.zipcode || profileForm.zipcode,
            basicAddress: data.user.basic_address || profileForm.basicAddress,
            detailAddress: data.user.detail_address || profileForm.detailAddress
          };
        }
        
        // 3초 후 메시지 제거
        setTimeout(() => {
          profileUpdateMessage = '';
        }, 3000);
      } else {
        profileUpdateMessage = data.message || '정보 업데이트에 실패했습니다.';
      }
    } catch (error) {
      console.error('사용자 정보 업데이트 오류:', error);
      profileUpdateMessage = '정보 업데이트 중 오류가 발생했습니다.';
    } finally {
      isProfileUpdating = false;
    }
  }
  
  // 파일 업로드 성공 핸들러
  function handleFileUploadSuccess(event) {
    const { files, message } = event.detail;
    alert(message);
    userFiles = [...userFiles, ...files];
  }
  
  // 파일 다운로드 핸들러
  async function handleFileDownload(event) {
    try {
      const { file } = event.detail;
      const token = localStorage.getItem('token');
      
      // 서버 스트리밍 다운로드 사용
      const downloadUrl = `${API_BASE_URL}/api/files/stream/${file.id}`;
      
      // 다운로드 링크 생성 및 클릭
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.style.display = 'none';
      
      // Authorization 헤더는 링크로 전달할 수 없으므로 fetch로 처리
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // 파일 데이터를 blob으로 변환
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // 파일명 추출 - RFC 5987 형식과 기본 filename 모두 처리
        const contentDisposition = response.headers.get('content-disposition');
        let filename = file.original_name || 'download';
        if (contentDisposition) {
          // RFC 5987 형식 (filename*=UTF-8'') 우선 처리
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
          if (utf8Match) {
            filename = decodeURIComponent(utf8Match[1]);
          } else {
            // 기본 filename 형식 처리
            const basicMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
            if (basicMatch) {
              filename = basicMatch[1];
            }
          }
        }
        
        // 다운로드 실행
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // URL 해제
        window.URL.revokeObjectURL(url);
      } else {
        alert('파일 다운로드에 실패했습니다.');
      }
    } catch (error) {
      console.error('파일 다운로드 오류:', error);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    }
  }
  
  // 파일 삭제 핸들러
  async function handleFileDelete(event) {
    try {
      if (!confirm('정말로 파일을 삭제하시겠습니까?')) {
        return;
      }
      
      const { file } = event.detail;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/files/${file.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        userFiles = userFiles.filter(f => f.id !== file.id);
        alert('파일이 삭제되었습니다.');
      } else {
        const errorData = await response.json();
        alert(`파일 삭제에 실패했습니다: ${errorData.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('파일 삭제 오류:', error);
      alert('파일 삭제 중 오류가 발생했습니다.');
    }
  }


  // 프로젝트 리스트에서 사용자가 지원한 공고 여부를 표시하는 함수
  function markAppliedProjects() {
    // projects 배열이 없거나 비어있으면 종료
    if (!projects || projects.length === 0) return;

    // 지원 내역이 없으면 모두 false 처리
    if (!applications || applications.length === 0) {
      projects.forEach(p => p.hasApplied = false);
    } else {
      // 지원한 프로젝트 ID 목록 추출
      const appliedProjectIds = applications
        .map(app => app.projectId || app.project_id || (app.project && app.project.id))
        .filter(id => id !== undefined && id !== null);

      // projects 배열에 hasApplied 플래그 설정
      projects.forEach(p => {
        p.hasApplied = appliedProjectIds.includes(p.id);
      });
    }

    // 반응형 업데이트를 위해 배열 재할당
    filteredProjects = [...filteredProjects];
  }

  let projectApplicantCounts = {}; // 프로젝트별 지원자 수 맵

  // 프로젝트별 지원자 수 조회 (관리자용)
  async function fetchProjectApplicantCounts() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/applicants/counts`);
      if (!response.ok) {
        throw new Error('지원자 수 데이터를 불러오는 데 실패했습니다.');
      }
      const data = await response.json();
      projectApplicantCounts = data.counts || {};

      // 프로젝트 객체에 지원자 수 추가
      projects.forEach(p => {
        p.applicantCount = projectApplicantCounts[p.id] || 0;
      });
      // 반응형 업데이트
      filteredProjects = [...filteredProjects];
    } catch (error) {
      console.error('프로젝트별 지원자 수 로딩 오류:', error);
    }
  }
</script>

{#if currentPage === 'login'}
  <Login setPage={setPage} setUser={setUser} />
{:else if currentPage === 'signup'}
  <Signup setPage={setPage} />
{:else if currentPage === 'signup-complete'}
  <SignupComplete setPage={setPage} />
{:else if currentPage === 'how-to-use'}
  <HowToUse setPage={setPage} user={user} isAdmin={isAdmin} />
{:else if currentPage === 'gemini-test'}
  <GeminiTest setPage={setPage} />
{:else if currentPage === 'project-register'}
  {#if isAdmin || isCompany}
    <ProjectRegister setPage={setPage} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'project-edit'}
  {#if isAdmin || isCompany}
    <ProjectEdit setPage={setPage} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'admin-applicants'}
  {#if isAdmin}
    <AdminApplicants setPage={setPage} user={user} onLogout={handleLogout} initialApplicantId={selectedApplicantId} initialShowDetail={selectedApplicantShowDetail} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'admin-project-applicants'}
  {#if isAdmin}
    <AdminProjectApplicants setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'admin-all-applicants'}
  {#if isAdmin}
    <AdminAllApplicants setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'company-applicants'}
  {#if isCompany || isAdmin}
    <CompanyApplicants setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'admin-users'}
  {#if isAdmin}
    <AdminUsers setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'admin-dashboard'}
  {#if isAdmin}
    <AdminDashboard setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'common-codes'}
  {#if isAdmin}
    <CommonCodes setPage={setPage} user={user} onLogout={handleLogout} />
  {:else}
    <Login setPage={setPage} setUser={setUser} />
  {/if}
{:else if currentPage === 'certificate-issue'}
  <CertificateIssue setPage={setPage} user={user} isAdmin={isAdmin} />
{:else}
<main class="app-container">
  <Navigation 
    {user}
    {isAdmin}
    {currentPage}
    onLogout={handleLogout}
  />

  {#if isLoading}
  <main class="loading-container">
    <div class="loader"></div>
    <p>프로젝트를 불러오는 중입니다...</p>
  </main>
  {:else if loadError}
  <main class="error-container">
    <div class="error-icon">!</div>
    <h2>데이터를 불러오지 못했습니다</h2>
    <p>{loadError}</p>
    <button class="retry-btn" on:click={fetchProjects}>다시 시도</button>
  </main>
  {:else}
  <div class="main-content">
    {#if currentPage === 'mypage'}
      <MyPage 
        {user}
        {mypageTab}
        {applications}
        {filteredApplications}
        {appStats}
        {activeFilter}
        {profileForm}
        {userFiles}
        {calculatedAge}
        {profileUpdateMessage}
        {isProfileUpdating}
        {statusOptions}
        {statusMapping}
        {reverseStatusMapping}
        onSetMypageTab={setMypageTab}
        onSetFilter={setFilter}
        onUpdateUserProfile={updateUserProfile}
        onHandleFileUploadSuccess={handleFileUploadSuccess}
        onHandleFileDownload={handleFileDownload}
        onHandleFileDelete={handleFileDelete}
      />
    {:else}
      <div class="main-content">
        {#if selectedProject}
          <ProjectDetail 
            {selectedProject}
            {user}
            {applications}
            {applyLoading}
            {applicationError}
            bind:projectPrice
            bind:availableDate
            bind:startWorkDate
            bind:applyMessage
            {statusOptions}
            {statusMapping}
            {reverseStatusMapping}
            onApplyToProject={applyToProject}
            onFetchProjectApplications={fetchProjectApplications}
            onSetPage={setPage}
            onCloseProjectDetail={closeProjectDetail}
            formatProjectPeriod={(startDate, endDate) => formatProjectPeriod(startDate, endDate, selectedProject.status)}
            formatRecruitmentPeriod={(createdAt, deadlineDate) => formatRecruitmentPeriod(createdAt, deadlineDate, selectedProject.status)}
            {validateNumber}
          />
        {:else}
          <div class="main-layout">
            <ProjectFilters 
              {filters}
              skillsData={skillsData}
              {expandedSkills}
              onResetFilters={resetFilters}
              onApplyFilters={filterProjects}
              onToggleSkill={toggleSkill}
              bind:searchTerm={searchTerm}
              onSearch={handleSearchClick}
              onKeyup={handleSearchKeyup}
            />
            
            <ProjectList 
              {filteredProjects}
              displayedProjects={displayedProjects}
              {searchTerm}
              {sortOption}
              {hasMoreItems}
              {isLoadingMore}
              isAdmin={isAdmin}
              onSortChange={handleSortChange}
              onViewProject={viewProjectDetail}
              onSearch={filterProjects}
              bind:intersectionTarget
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>
  {/if}
</main>

<footer class="main-footer">
  <div class="company-info">
    <h3>주식회사 비전와이드</h3>
    <p>대표자명 : 최병영 | 사업자등록번호 : 134-87-40607 | 통신판매업신고 : 제2023-서울구로-2412호</p>
    <p>서울특별시 구로구 디지털로34길 55, B201-H4호 (구로동, 코오롱싸이언스밸리2차)</p>
    <p>TEL : 02-565-5747 | FAX : 02-6499-5747 | E-mail : admin@visionwide.kr</p>
    <p>©VISIONWIDE. ALL RIGHTS RESERVED.</p>
  </div>
</footer>
{/if}

{#if isDetailModalOpen && selectedApplication}
  <BaseModal on:close={closeDetailModal}>
    <div class="detail-section">
      <h4>지원 정보</h4>
      <div class="detail-grid">
        <div class="detail-item">
          <div class="detail-label">프로젝트명</div>
          <div class="detail-value">
            {selectedApplication.project_title || selectedApplication.projectTitle || selectedApplication.projectName || selectedApplication.title || 
             (selectedApplication.project && selectedApplication.project.title) || '프로젝트명 없음'}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">지원일</div>
          <div class="detail-value">
            {#if selectedApplication.appliedAt || selectedApplication.applied_at || selectedApplication.createdAt || selectedApplication.created_at || selectedApplication.date}
              {new Date(selectedApplication.appliedAt || selectedApplication.applied_at || selectedApplication.createdAt || selectedApplication.created_at || selectedApplication.date).toLocaleDateString()}
            {:else}
              -
            {/if}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">상태</div>
          <div class="detail-value">
            {#if typeof selectedApplication.status === 'number'}
              <span class="status-badge {reverseStatusMapping[selectedApplication.status] || ''}">
                {statusOptions.find(option => option.id === selectedApplication.status)?.label || '알 수 없음'}
              </span>
            {:else if typeof selectedApplication.status === 'string' && statusMapping[selectedApplication.status] !== undefined}
              <span class="status-badge {selectedApplication.status}">
                {statusOptions.find(option => option.id === statusMapping[selectedApplication.status])?.label || '알 수 없음'}
              </span>
            {:else}
              <span class="status-badge {selectedApplication.status || 'applied'}">
                {selectedApplication.status === 'applied' ? '지원완료' : 
                 selectedApplication.status === 'reviewing' ? '검토중' : 
                 selectedApplication.status === 'closed' ? '마감' : 
                 selectedApplication.status === 'canceled' ? '지원 취소' : 
                 selectedApplication.status || '알 수 없음'}
              </span>
            {/if}
          </div>
        </div>
        <div class="detail-item">
          <div class="detail-label">단가</div>
          <div class="detail-value">
            {selectedApplication.price ? `${selectedApplication.price}만원` : '-'}
          </div>
        </div>
      </div>
    </div>
    
    {#if selectedApplication.message}
      <div class="detail-section">
        <h4>지원 메시지</h4>
        <div class="message-box">
          {selectedApplication.message}
        </div>
      </div>
    {/if}
    
    <div class="detail-modal-actions">
      <Button variant="secondary" on:click={closeDetailModal}>닫기</Button>
      {#if selectedApplication.status === 'applied' || selectedApplication.status === 'reviewing'}
        <Button variant="danger" on:click={() => cancelApplication(selectedApplication.id)}>
          지원 취소
        </Button>
      {/if}
    </div>
  </BaseModal>
{/if}
